import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { api } from "../api";
import Switch from "./automations/Switch";
import BookingsTab from "./booking/BookingsTab";
import PageTab from "./booking/PageTab";
import AvailabilityLink from "./AvailabilityLink";
import BookingLogTab from "./booking/BookingLogTab";
import CopyButton from "./booking/CopyButton";
import "./booking/bookingAdmin.css";

/*
 * THE BOOKING PAGE, ADMIN SIDE. Drawn to "Remotion CRM - Booking Page.pptx"
 * (Claude Design, 2026-09-29): the Automations frame of one switch, a stat
 * row, then tabs. Bookings, Page, Calendar, Log.
 *
 * The switch is refused by the server until Google can write to the calendar,
 * and the header says so with a checklist rather than a switch that fails.
 */

const TABS = [
  { key: "bookings", label: "Bookings" },
  { key: "page", label: "Page" },
  { key: "log", label: "Log" },
];

function reconnectHref() {
  let token = "";
  try {
    token = localStorage.getItem("dashboard_token") || "";
  } catch {
    token = "";
  }
  return `/api/gmail/auth?redirect=1&token=${encodeURIComponent(token)}`;
}



function Readiness({ readiness, onChanged, setError }) {
  const [editing, setEditing] = useState(false);
  const [origin, setOrigin] = useState(readiness.base_url || "https://crm.remotion.io");
  const [busy, setBusy] = useState(false);

  const missing = [!readiness.calendar_write, !readiness.base_url, !readiness.personal_links].filter(Boolean).length;
  if (!missing) return null;

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      onChanged(await fn());
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="crm-bkready">
      <b>
        {missing} thing{missing === 1 ? "" : "s"} before leads can book
      </b>
      <div className="crm-bkready__row">
        <Mark ok={readiness.calendar_write} />
        <span>Google can add events to your calendar</span>
        {!readiness.calendar_write && (
          <a className="crm-btn crm-btn--dark" href={reconnectHref()}>
            Reconnect Google
          </a>
        )}
      </div>
      <div className="crm-bkready__row">
        <Mark ok={!!readiness.base_url} />
        {editing ? (
          <form
            className="crm-bkready__form"
            onSubmit={(e) => {
              e.preventDefault();
              run(async () => {
                const next = await api("/booking/settings", { method: "PUT", body: JSON.stringify({ base_url: origin }) });
                setEditing(false);
                return next;
              });
            }}
          >
            <input className="crm-input" value={origin} onChange={(e) => setOrigin(e.target.value)} autoFocus />
            <button className="crm-btn crm-btn--dark" disabled={busy}>
              Save
            </button>
          </form>
        ) : (
          <span className={readiness.base_url ? "crm-bkready__done" : ""}>
            {readiness.base_url ? `Public address set: ${readiness.base_url.replace(/^https?:\/\//, "")}` : "Public address"}
          </span>
        )}
        {!editing && !readiness.base_url_pinned && (
          <button type="button" className="crm-btn crm-btn--secondary" onClick={() => setEditing(true)}>
            {readiness.base_url ? "Change" : "Set"}
          </button>
        )}
      </div>
      <div className="crm-bkready__row">
        <Mark ok={readiness.personal_links} />
        <span className={readiness.personal_links ? "crm-bkready__done" : ""}>Personal links on</span>
        {!readiness.personal_links && (
          <button
            type="button"
            className="crm-btn crm-btn--dark"
            disabled={busy}
            onClick={() => run(() => api("/booking/personal-links", { method: "POST" }))}
          >
            Turn on
          </button>
        )}
      </div>
    </div>
  );
}

function Mark({ ok }) {
  return <span className={`crm-bkmark ${ok ? "is-ok" : "is-missing"}`}>{ok ? "✓" : "!"}</span>;
}

function StatRow({ stats }) {
  if (!stats) return <div className="crm-bkstats crm-bkstats--loading" />;
  const mix = [
    ["plan", stats.intent_plan, "crm-bkbar--plan"],
    ["fix", stats.intent_fix, "crm-bkbar--fix"],
    ["fit", stats.intent_fit, "crm-bkbar--fit"],
    ["none", stats.intent_none, "crm-bkbar--none"],
  ];
  const mixTotal = mix.reduce((n, [, v]) => n + v, 0) || 1;
  const anonymous = stats.booked - stats.personal;
  const days = stats.median_hours_ahead == null ? null : Math.round(stats.median_hours_ahead / 24);
  return (
    <div className="crm-bkstats">
      <div className="crm-bkstat">
        <span className="crm-bkstat__k">Booked</span>
        <span className="crm-bkstat__v crm-bkstat__v--blue">{stats.booked}</span>
      </div>
      <div className="crm-bkstat">
        <span className="crm-bkstat__k">Cancelled</span>
        <span className="crm-bkstat__v">{stats.cancelled}</span>
      </div>
      <div className="crm-bkstat">
        <span className="crm-bkstat__k">Moved</span>
        <span className="crm-bkstat__v">{stats.moved}</span>
      </div>
      <div className="crm-bkstat">
        <span className="crm-bkstat__k">Median days ahead</span>
        <span className="crm-bkstat__v">{days == null ? "—" : `${days} day${days === 1 ? "" : "s"}`}</span>
      </div>
      <div className="crm-bkstat crm-bkstat--wide">
        <span className="crm-bkstat__k">Agenda mix</span>
        <div className="crm-bkbar">
          {mix.map(([k, v, cls]) => (v ? <span key={k} className={cls} style={{ flex: v / mixTotal }} /> : null))}
        </div>
        <span className="crm-bkstat__legend">
          {mix.map(([k, v, cls]) => (
            <span key={k}>
              <i className={cls} /> {k} <b>{v}</b>
            </span>
          ))}
        </span>
      </div>
      <div className="crm-bkstat crm-bkstat--wide">
        <span className="crm-bkstat__k">Personal vs anonymous</span>
        <div className="crm-bkbar crm-bkbar--pa">
          {stats.booked ? <span className="crm-bkbar--personal" style={{ flex: stats.personal / stats.booked }} /> : null}
        </div>
        <span className="crm-bkstat__legend">
          <b>{stats.personal}</b>&nbsp;personal · <b>{anonymous}</b>&nbsp;anonymous
        </span>
      </div>
    </div>
  );
}

export default function BookingAdminPage() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "bookings";
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [days, setDays] = useState(30);
  const [error, setError] = useState(null);
  const [switching, setSwitching] = useState(false);

  const loadSettings = useCallback(() => api("/booking/settings").then(setData).catch((e) => setError(e.message)), []);
  const loadStats = useCallback(() => api(`/booking/stats?days=${days}`).then(setStats).catch(() => {}), [days]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // The Calendar tab moved to Meetings ▸ Availability; an old link lands there.
  if (params.get("tab") === "calendar") return <Navigate to="/meetings/availability" replace />;

  if (!data) {
    return (
      <div className="crm-auto crm-bk">
        {error ? <div className="crm-card crm-card--warning">{error}</div> : <p className="crm-hint">Loading…</p>}
      </div>
    );
  }

  const { settings, readiness } = data;
  const on = settings.enabled;
  const canTurnOn = readiness.calendar_write;

  const toggle = async (next) => {
    setSwitching(true);
    setError(null);
    try {
      setData(await api("/booking/settings", { method: "PUT", body: JSON.stringify({ enabled: next }) }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div className="crm-auto crm-auto--detail crm-bk">
      <header className="crm-bkhead">
        <div className="crm-bkhead__left">
          <div className="crm-auto-head__left">
            <h1 className="crm-auto-title">Booking page</h1>
            <span className={`crm-pill ${on ? "crm-pill--active" : "crm-pill--draft"}`}>{on ? "Active" : "Off"}</span>
          </div>
          <div className="crm-bkhead__url">
            {data.public_url ? (
              <a href={data.public_url} target="_blank" rel="noreferrer">
                {data.public_url.replace(/^https?:\/\//, "")}
              </a>
            ) : (
              <span className="crm-hint">No public address yet</span>
            )}
            <CopyButton text={data.public_url} />
            <span className="crm-hint">Leads open it from your emails and your invite.</span>
          </div>
        </div>
        <Readiness readiness={readiness} onChanged={setData} setError={setError} />
        <div className="crm-bkhead__switch">
          <label className="crm-auto-switch">
            <span>{on ? "ON" : "OFF"}</span>
            <Switch
              on={on}
              pending={switching || (!on && !canTurnOn)}
              label={on ? "Turn the booking page off" : "Turn the booking page on"}
              onChange={toggle}
            />
          </label>
          <span className="crm-hint">{on ? "Leads can book" : canTurnOn ? "Leads see “booking is paused”" : "Connect Google to turn it on"}</span>
        </div>
      </header>

      {error && <div className="crm-card crm-card--warning crm-auto-error">{error}</div>}

      <div className="crm-bkrange">
        <span className="crm-bkmono">Last {days} days</span>
        <span className="crm-bkseg">
          {[30, 90].map((d) => (
            <button key={d} type="button" className={d === days ? "sel" : ""} onClick={() => setDays(d)}>
              {d} days
            </button>
          ))}
        </span>
      </div>
      <StatRow stats={stats} />

      <AvailabilityLink />

      <div className="crm-auto-tabs" role="tablist">
        {TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            role="tab"
            aria-selected={tab === entry.key}
            className={`crm-auto-tab${tab === entry.key ? " is-active" : ""}`}
            onClick={() => setParams({ tab: entry.key }, { replace: true })}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <div className="crm-auto-tabbody" role="tabpanel">
        {tab === "bookings" && <BookingsTab data={data} onChanged={loadStats} />}
        {tab === "page" && <PageTab data={data} onSaved={setData} />}
        {tab === "log" && <BookingLogTab data={data} days={days} />}
      </div>
    </div>
  );
}
